import * as React from 'react';
import { Link } from 'react-router';
import styled, { keyframes } from 'styled-components';
import { rem } from 'polished';
import { TextP } from '../../Defaults/Text';
import {
  FlexColCenter,
  FlexCol,
  FlexRowCenterVertical,
  FlexBetween,
} from '../../Defaults/Flexbox/Flexbox';
import { Tag, TagType } from './ProductTag';
import { ButtonLink } from '../General/Button';
import { StockInfo } from './StockInfo';
import {
  getProductDetailInfo,
  getElasticCategoryProductInfo,
} from '../../../utilities/selectors';
import { getSanitasProductDefaultInfo } from '../selectors';
import { formatPrice, prop } from '../../../utilities';
import { __ } from 'react-i18n';
import ProductLabels from '../ProductDetail/ProductLabels';
import { AddToWishlsit } from '../ProductDetail/AddToWishlsit';

export interface Props {
  product: any;
  isElasticCategory: boolean;
  addToWishList: (productId: any, goodId: any, add: any) => void;
  showWishList?: boolean;
}

function ProductTabDefault({
  product,
  isElasticCategory,
  addToWishList,
  showWishList = true,
}: Props) {
  const {
    onStock,
    url,
    price,
    oldPrice,
    unit,
    availability,
    orderAvailable,
    packageInfo,
  } = isElasticCategory
    ? getElasticCategoryProductInfo(product)
    : getProductDetailInfo(product);

  const { name, issale, isnew } = isElasticCategory
    ? getElasticCategoryProductInfo(product)
    : getProductDetailInfo(product);

  const { image } = getSanitasProductDefaultInfo(product);

  const lastInStock = onStock === 1 ? true : false;

  const isTag = isnew === 1 || issale === 1 || lastInStock;
  const productLabels =
    prop(product, 'product_detail.product_label', null) ||
    prop(product, 'product_label', null);
  const productLabel = prop(productLabels, '0.text', null);

  const salePercentage = oldPrice
    ? Math.abs(-Math.round((1 - +price / oldPrice) * 100))
    : null;

  const isInWishlist =
    prop(product, 'product_detail.is_in_wishlist') ||
    prop(product, 'is_in_wishlist') ||
    false;

  const handleAddToWishList = add => {
    const goodId =
      prop(product, 'main_good_id') || prop(product, 'main_good.good_id', null);
    if (goodId) {
      addToWishList(product.product_id, goodId, add);
    }
  };

  const handleOnClick = add => {
    handleAddToWishList(add);
  };

  return (
    <Wrapper>
      <BottomBorder />
      {showWishList ? (
        <AddToWishlistWrapper>
          <AddToWishlsit
            addedToWishlist={isInWishlist}
            onClick={handleOnClick}
            catalog={true}
          />
        </AddToWishlistWrapper>
      ) : null}
      <RightBorder />
      <TagsWrapper>
        {isTag ? (
          <>
            {isnew ? <Tag type={TagType.NEW} text={'NOVINKA'} /> : null}
            {/* {issale ? (
              <Tag
                style={{ marginLeft: isnew ? rem(8) : 0 }}
                type={TagType.ACTION}
                text={'AKCIA'}
              />
            ) : null} */}
            {lastInStock ? (
              <Tag type={TagType.LASTONSTOCK} text={'POSLEDNÝ KUS'} />
            ) : null}
            {productLabel ? (
              <Tag type={TagType.LABEL} text={productLabel} />
            ) : null}
          </>
        ) : null}
      </TagsWrapper>
      <TopWrapper to={url}>
        <Image alt={`Obrázok ku produktu ${name}`} src={image} />
        <Name>{name}</Name>
        <PriceCol>
          <PriceRow>
            {price ? (
              <Price>
                {salePercentage ? (
                  <SalePercentage>-{salePercentage} %</SalePercentage>
                ) : null}
                {formatPrice(price, 'EUR')}
              </Price>
            ) : (
              <NoPrice>{__('Cena na vyžiadanie')}</NoPrice>
            )}
            {price && oldPrice ? (
              <OldPrice>{formatPrice(oldPrice, 'EUR')}</OldPrice>
            ) : null}
          </PriceRow>
        </PriceCol>
      </TopWrapper>
      <BottomWrapper>
        <DetailButton to={url}>Detail produktu</DetailButton>
        <StockInfo
          onStock={onStock}
          unit={unit}
          availability={availability}
          orderAvailable={orderAvailable}
        />
        {packageInfo && false && (
          <SecondaryText>
            {__('Balenie')}: {packageInfo}
          </SecondaryText>
        )}
      </BottomWrapper>
    </Wrapper>
  );
}

export const animateIn = keyframes`
   0% {
    opacity: 0;
    transform: scale(0.6) translateY(-8px);
  }
  
  100% {
    opacity: 1;
  }
`;

const BottomBorder = styled.div`
  border-bottom: ${({ theme }) => theme.borders.productList};
  position: absolute;
  left: ${rem(0)};
  right: ${rem(0)};
  bottom: 0;

  ${({ theme }) => theme.mediab.s450`
    left: ${rem(0)};
    right: ${rem(0)};
  `}
`;

const RightBorder = styled.div`
  border-right: ${({ theme }) => theme.borders.productList};
  position: absolute;
  bottom: ${rem(0)};
  top: ${rem(0)};
  right: 0;

  ${({ theme }) => theme.mediab.s450`
    bottom: ${rem(0)};
    top: ${rem(0)};
  `}
`;

const Wrapper = styled(FlexColCenter)`
  animation: ${animateIn} 0.3s ease;
  position: relative;
  /* width: ${rem(345)}; */
  width: 33.3333%;
  padding-bottom: ${rem(24)};
  padding-top: ${rem(24)};
  background-color: ${({ theme }) => theme.colors.white};

  ${({ theme }) => theme.mediab.l1400`
    min-width: 50%;
    width: 50%;
    max-width: initial;
  `}

  ${({ theme }) => theme.mediab.s450`
    padding-top: ${rem(32)};
    padding-bottom: ${rem(32)};
  `}

  &:nth-child(3n) > :nth-child(2) {
    border: 0;
  }

    
  &:nth-last-child(-n + 3):nth-child(3n + 1),
  &:nth-last-child(-n + 3):nth-child(3n + 1) ~ &
  {
    ${BottomBorder}{
      border-bottom: 0;
    }
  }

  ${({ theme }) => theme.mediab.l1400`

    &:nth-child(3n) > :nth-child(2) {
      border-right: ${theme.borders.productList};
    }

    &:nth-child(2n) > :nth-child(2) {
      border: 0;
    }
    &:nth-last-child(-n + 3):nth-child(3n + 1),
    &:nth-last-child(-n + 3):nth-child(3n + 1) ~ &
    {
      ${BottomBorder}{
        border-bottom: ${theme.borders.productList};
      }
    }
    &:nth-last-child(-n + 2):nth-child(2n + 1),
    &:nth-last-child(-n + 2):nth-child(2n + 1) ~ &
    {
      ${BottomBorder}{
        border-bottom: 0;
      }
    }
  `}
`;

const TopWrapper = styled(Link)`
  position: relative;
  top: 0;
  display: flex;
  flex-flow: column;
  align-items: center;
  width: 100%;
  height: ${rem(300)};
  max-height: ${rem(360)};
  text-decoration: none;
  margin-bottom: ${rem(16)};

  ${({ theme }) => theme.mediab.s450`
    height: initial;
    margin-bottom: ${rem(8)};
  `}
`;

const AddToWishlistWrapper = styled.div`
  position: absolute;
  right: ${rem(24)};
  top: ${rem(24)};
  z-index: 199;
  ${({ theme }) => theme.mediab.l1300`
  top: 20%;
`}
`;

const BottomWrapper = styled(FlexColCenter)`
  width: 100%;
  justify-content: space-between;
`;

const Image = styled.img`
  margin-bottom: ${rem(24)};
  width: auto;
  height: 100%;
  transition: transform 0.3s ease-out;

  &:hover {
    transform: scale(0.9);
  }

  ${({ theme }) => theme.mediab.s450`
    width: ${rem(128)};
    height: ${rem(128)};
    margin-top: 0;
  `}
`;

const Name = styled(TextP)`
  max-width: 80%;
  font-size: ${rem(18)};
  font-family: 'Roboto', sans-serif;
  font-weight: 500;
  color: #003463;
  line-height: ${rem(18)};
  letter-spacing: 0px;
  opacity: 1;
  text-align: center;
  margin: 0 auto;
  height: auto;

  ${({ theme }) => theme.mediab.s450`
    font-size: ${rem(14)};
  `}
  &:hover {
    color: ${({ theme }) => theme.colors.primary};
    text-decoration: underline;
  }
`;

const PriceCol = styled(FlexCol)`
  margin: ${rem(8)} auto 0 auto;
`;

const PriceRow = styled(FlexRowCenterVertical)`
  ${({ theme }) => theme.mediab.s450`
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    padding: 0 ${rem(4)}
    `}
`;

const Price = styled(TextP)`
  font-family: 'Roboto', sans-serif;
  font-weight: 700;
  font-size: ${rem(24)};
  line-height: ${rem(28)};
  color: #000000;
  white-space: nowrap;
  display: flex;
  ${({ theme }) => theme.mediab.s450`
  font-size: ${rem(18)};
`}
`;

const NoPrice = styled(Price)`
  font-size: ${rem(20)};
  color: ${({ theme }) => theme.colors.primary};
`;

const TagsWrapper = styled(FlexBetween)`
  position: absolute;
  top: ${rem(24)};
  left: 8%;
  z-index: 110;
  max-width: calc(100% - ${rem(90)});
  ${({ theme }) => theme.mediab.s450`
    display: flex;
    flex-flow: column;
    align-items: flex-start;
    top: ${rem(12)};
    flex-direction: column-reverse;
  `}
  ${({ theme }) => theme.mediab.l1400`
  `}

  display: flex;
  flex-wrap: wrap;
  justify-content: flex-start;
`;

const SecondaryText = styled(TextP)`
  font-family: ${({ theme }) => theme.font.primary};
  font-size: ${rem(12)};
  line-height: ${rem(20)};
  color: ${({ theme }) => theme.colors.placeholder};
`;

const OldPrice = styled(SecondaryText)`
  margin-left: ${rem(8)};
  text-decoration: line-through;
  white-space: nowrap;

  ${({ theme }) => theme.mediab.s450`
  `}
`;

const DetailButton = styled(ButtonLink)`
  height: ${rem(32)};
  background-color: ${({ theme }) => theme.colors.primary};
  padding: ${rem(6)} ${rem(12)} ${rem(6)} ${rem(12)};
  margin: 0 auto ${rem(16)} auto;
  border: 2px solid ${({ theme }) => theme.colors.primary};
  border-radius: ${rem(4)};
  font-size: ${rem(14)};
  line-height: ${rem(28)};
  text-transform: none;
  min-width: ${rem(143)};

  font-family: 'Roboto', sans-serif;
  font-weight: 500;
  font-size: ${rem(14)};
  color: ${({ theme }) => theme.colors.white};

  &:hover {
    transition: background-color 0.2s ease;
    background-color: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.white};
  }

  ${({ theme }) => theme.mediab.s450`
    display: none;
  `}
`;
const SalePercentage = styled.div`
  background-color: #fa352f;
  border-radius: ${rem(4)};
  font-size: 12px;
  font-weight: 600;
  color: #fff;
  padding: ${rem(7)} ${rem(9)};
  line-height: ${rem(14)};
  margin-right: ${rem(8)};
`;

export { ProductTabDefault as SanitasProductTab };
